import React from "react";
import { Box, Typography, Chip, LinearProgress, Stack, Button } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { useSubscription } from "../Hooks/useSubscription";

// TODO: make this plan-configurable via a
// `features.invoice.warnWhenRemainingLessThan` field on the plan document if
// you want per-plan thresholds later — kept as a constant for now per
// requirement #16 ("make configurable if practical").
const WARNING_THRESHOLD_REMAINING = 3;

interface IProps {
  /** Path to your existing subscription/upgrade page, if one exists.
   *  Per requirement #16, don't invent a payment flow — omit this prop
   *  if no such page exists yet and the Upgrade button simply won't render. */
  upgradePagePath?: string;
}

const SubscriptionUsageBanner: React.FC<IProps> = ({ upgradePagePath }) => {
  const { plan, isActive, isExpired, invoiceUsage, loading } = useSubscription();

  if (loading || !plan) return null;

  if (isExpired) {
    return (
      <Box sx={{ p: 1.5, mb: 2, borderRadius: 2, bgcolor: "#fdecea", border: "1px solid #f5c6cb" }}>
        <Typography sx={{ color: "#c62828" }} fontWeight={600}>
          Subscription Expired
        </Typography>
        <Typography variant="body2" sx={{ color: "#c62828" }}>
          Please renew your plan to continue creating invoices.
        </Typography>
        {upgradePagePath && (
          <Button size="small" component={RouterLink} to={upgradePagePath} sx={{ mt: 1 }}>
            Renew Plan
          </Button>
        )}
      </Box>
    );
  }

  if (!isActive || !invoiceUsage) return null;

  const nearLimit = !invoiceUsage.unlimited && (invoiceUsage.remaining ?? 0) <= WARNING_THRESHOLD_REMAINING;
  const atLimit = !invoiceUsage.unlimited && (invoiceUsage.remaining ?? 0) <= 0;

  return (
    <Box
      sx={{
        // NOTE: was "xp: 1.5" (typo, not a real sx key — silently ignored,
        // so the box had no padding at all). Fixed to "p".
        p: 1.5,
        mb: 2,
        borderRadius: 2,
        bgcolor: atLimit ? "#fdecea" : nearLimit ? "#fff8e1" : "#fafafa",
        border: "1px solid",
        borderColor: atLimit ? "#f5c6cb" : nearLimit ? "#ffe082" : "#eeeeee",
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip size="small" label={plan.planName} />
          {/* FIX: explicit color instead of inheriting from whatever
              container this banner is dropped into (e.g. OrgDetailsPopup's
              modal CSS, which sets a light/white text color that made this
              line invisible with no color of its own). */}
          <Typography variant="body2" sx={{ color: "#212121" }}>
            {invoiceUsage.unlimited
              ? "Invoices: Unlimited"
              : `Invoices: ${invoiceUsage.used} / ${invoiceUsage.limit} used`}
          </Typography>
        </Stack>
        {!invoiceUsage.unlimited && upgradePagePath && (nearLimit || atLimit) && (
          <Button size="small" variant="outlined" component={RouterLink} to={upgradePagePath}>
            Upgrade Plan
          </Button>
        )}
      </Stack>

      {!invoiceUsage.unlimited && (
        <LinearProgress
          variant="determinate"
          value={Math.min(100, (invoiceUsage.used / Math.max(invoiceUsage.limit || 1, 1)) * 100)}
          color={atLimit ? "error" : nearLimit ? "warning" : "primary"}
          sx={{ mt: 1, height: 6, borderRadius: 3 }}
        />
      )}

      {atLimit && (
        <Typography variant="caption" display="block" mt={0.5} sx={{ color: "#c62828" }}>
          You have reached your invoice limit. Please upgrade your plan to create more invoices.
        </Typography>
      )}
      {!atLimit && nearLimit && (
        <Typography variant="caption" display="block" mt={0.5} sx={{ color: "#616161" }}>
          You have {invoiceUsage.remaining} invoice{invoiceUsage.remaining === 1 ? "" : "s"} remaining on your
          current plan.
        </Typography>
      )}
    </Box>
  );
};

export default SubscriptionUsageBanner;